import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

// Raw field names as returned by enrich_seller_profile (DRS's
// SELLER_WEBSITE_PROMPT) — snake_case, some list-valued. Grouped into
// sections matching the design reference's SectionBlock layout.
const TEXT_FIELDS = [
  { key: "company_name", label: "Company name" },
  { key: "location", label: "Location" },
  { key: "website", label: "Website" },
  { key: "product_name", label: "Product name" },
  { key: "product_summary", label: "Product summary" },
  { key: "what_it_does", label: "What it does" },
  { key: "goal_of_outreach", label: "Goal of outreach" },
  { key: "preferred_messaging_tone", label: "Messaging tone" },
  { key: "sales_motion_style", label: "Sales motion style" }
];

const LIST_FIELDS = [
  { key: "main_value_prop", label: "Main value prop" },
  { key: "key_use_cases", label: "Key use cases" },
  { key: "current_customers", label: "Current customers" },
  { key: "competitive_edge", label: "Competitive edge" }
];

const asList = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const FieldRow = ({ label, value, onChange }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="copilotSellerProfile__field">
      <div className="copilotSellerProfile__fieldLabel">{label}</div>
      <div className="copilotSellerProfile__fieldRow">
        {editing ? (
          <textarea
            className="copilotSellerProfile__fieldInput"
            value={value || ""}
            autoFocus
            onChange={event => onChange(event.target.value)}
            onBlur={() => setEditing(false)}
          />
        ) : (
          <span className="copilotSellerProfile__fieldValue">
            {value || <em>Not set</em>}
          </span>
        )}
        <i
          className="material-icons-outlined copilotSellerProfile__editIcon"
          onClick={() => setEditing(true)}
        >
          edit
        </i>
      </div>
    </div>
  );
};

const PillList = ({ label, items, onChange }) => {
  const [draft, setDraft] = useState("");
  const remove = index => onChange(items.filter((_, i) => i !== index));
  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setDraft("");
  };
  return (
    <div className="copilotSellerProfile__field">
      <div className="copilotSellerProfile__fieldLabel">{label}</div>
      <div className="copilotSellerProfile__pills">
        {items.map((item, index) => (
          <span className="copilotSellerProfile__pill" key={`${item}_${index}`}>
            {item}
            <i
              className="material-icons-outlined"
              onClick={() => remove(index)}
            >
              close
            </i>
          </span>
        ))}
        <input
          className="copilotSellerProfile__pillInput"
          placeholder="Add…"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      </div>
    </div>
  );
};

/**
 * Right-side editable seller-profile draft (SG-2182) — same panel pattern
 * as SequencePreviewPanel, opened via copilotStore.previewPanel /
 * activePreviewBlock. Unlike the sequence preview (view-only), this panel
 * is fully editable: edits write directly onto the observable block object
 * (block.profile), not local-only state, so they survive closing and
 * reopening the panel. Approve/discard act on whatever's currently there.
 */
const BUILD_STAGES = [
  "Reading the website…",
  "Identifying what you sell…",
  "Mapping your target audience…",
  "Drafting positioning & value props…"
];

const SellerProfilePreviewPanel = observer(() => {
  const block = copilotStore.activePreviewBlock;
  if (!block) return null;
  const building = block.status === "building";
  const profile = block.profile || {};

  const setField = key => value => {
    block.profile = { ...(block.profile || {}), [key]: value };
  };
  const setListField = key => list => {
    block.profile = { ...(block.profile || {}), [key]: list };
  };

  const approve = () => copilotStore.approveSellerProfile(block.id, block.profile || {});
  const discard = () => copilotStore.discardSellerProfile(block.id);

  return (
    <div className="copilotTablePanel copilotPreviewPanel copilotSellerPanel">
      <div className="copilotTablePanel__header">
        <div className="copilotTablePanel__headMeta">
          <div className="copilotTablePanel__title">
            {building ? <span className="copilotLiveCard__liveDot" /> : <i className="material-icons-outlined">storefront</i>}
            Seller profile draft
          </div>
          <div className="copilotTablePanel__count">
            {building
              ? "Researching in realtime…"
              : profile.website
                ? `Sourced from ${profile.website}`
                : "Review and edit before saving"}
          </div>
        </div>
        <span
          className="material-icons-outlined copilotTablePanel__close"
          onClick={copilotStore.closePreviewPanel}
        >
          close
        </span>
      </div>
      {!block.done && !building && (
        <div className="copilotPreviewPanel__banner copilotSellerPanel__banner">
          <i className="material-icons-outlined">edit</i>
          <span>Edit any field below, then Approve &amp; save or Discard.</span>
        </div>
      )}
      <div className="copilotTablePanel__body copilotPreviewPanel__body">
        {building ? (
          <div className="copilotSellerBuild">
            {(() => {
              const currentIndex = Math.max(BUILD_STAGES.indexOf(block.stage), 0);
              return BUILD_STAGES.map((label, index) => {
                const isDone = index < currentIndex;
                const isCurrent = index === currentIndex;
                return (
                  <div
                    key={label}
                    className={`copilotSellerBuild__row ${isCurrent ? "is-current" : ""} ${isDone ? "is-done" : ""}`}
                  >
                    <span className="copilotSellerBuild__marker">
                      {isDone ? (
                        <i className="material-icons-outlined">check_circle</i>
                      ) : isCurrent ? (
                        <span className="copilotSellerBuild__spinner" />
                      ) : (
                        <span className="copilotSellerBuild__dot" />
                      )}
                    </span>
                    <span className="copilotSellerBuild__label">{label}</span>
                  </div>
                );
              });
            })()}
            <div className="copilotSeqPreview__typing">
              <span className="copilotDot" />
              <span className="copilotDot" />
              <span className="copilotDot" />
              <span className="copilotSeqPreview__typingLabel">{block.stage || "Researching…"}</span>
            </div>
          </div>
        ) : block.done ? (
          <div className="copilotSeqPreview__done">
            <i className="material-icons-outlined">
              {block.discarded ? "delete_outline" : block.saved === false ? "error_outline" : "check_circle"}
            </i>
            <span>
              {block.discarded
                ? "Discarded — no seller profile was saved."
                : block.saved === false
                  ? "Seller profile save failed."
                  : "Seller profile saved."}
            </span>
          </div>
        ) : (
          <div className="copilotSellerProfile__body">
            <div className="copilotSellerProfile__section">
              <div className="copilotSellerProfile__sectionTitle">Company</div>
              {TEXT_FIELDS.slice(0, 3).map(f => (
                <FieldRow key={f.key} label={f.label} value={profile[f.key]} onChange={setField(f.key)} />
              ))}
            </div>
            <div className="copilotSellerProfile__section">
              <div className="copilotSellerProfile__sectionTitle">What you sell</div>
              {TEXT_FIELDS.slice(3, 6).map(f => (
                <FieldRow key={f.key} label={f.label} value={profile[f.key]} onChange={setField(f.key)} />
              ))}
            </div>
            <div className="copilotSellerProfile__section">
              <div className="copilotSellerProfile__sectionTitle">Value &amp; use cases</div>
              {LIST_FIELDS.map(f => (
                <PillList
                  key={f.key}
                  label={f.label}
                  items={asList(profile[f.key])}
                  onChange={setListField(f.key)}
                />
              ))}
            </div>
            <div className="copilotSellerProfile__section">
              <div className="copilotSellerProfile__sectionTitle">Positioning</div>
              {TEXT_FIELDS.slice(6, 9).map(f => (
                <FieldRow key={f.key} label={f.label} value={profile[f.key]} onChange={setField(f.key)} />
              ))}
            </div>
          </div>
        )}
      </div>
      {!block.done && !building && (
        <div className="copilotSellerProfile__footer">
          <button type="button" className="copilotSellerProfile__approveBtn" onClick={approve}>
            <i className="material-icons-outlined">check</i>
            Approve &amp; save
          </button>
          <button type="button" className="copilotSellerProfile__discardBtn" onClick={discard}>
            <i className="material-icons-outlined">close</i>
            Discard
          </button>
        </div>
      )}
    </div>
  );
});

export default SellerProfilePreviewPanel;
